// SiloIconCard — Feb 23, 2026.
//
// Lets a creator set a custom 192×192 icon for a silo, used by:
//   1. iOS Safari → Add to Home Screen (apple-touch-icon on silo routes).
//   2. Android Chrome PWA installs (dynamic manifest icons[]).
//
// Storage: a small PNG data URL on the silo document (capped ~150KB server
// side). Delivery to the OS is via /api/s/<slug>/icon-<size>.png — iOS
// ignores data: URIs, which is why a creator's choice used to lose to the
// site logo.
//
// Jun 2026 (Randy pt-home-icon-fidelity) — preset row grew 5 → 16 (most of
// them monogram/abstract marks that take the silo's own initial, so the icon
// reads as the creator's rather than My Silo's), plus a picker that turns the
// silo's existing Public Q&A artwork into an icon.
import { useEffect, useMemo, useRef, useState } from "react";
import { Image as ImageIcon, UploadSimple, Trash, Spinner, DeviceMobile, Bell } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import SiloIconArtwork from "@/components/SiloIconArtwork";
import { PRESETS, renderPresetToDataUrl, siloLetter } from "@/lib/iconPresets";

const ICON_SIZE = 192; // px

// Oct 2026 (Randy pt-launch-target) — the only four destinations exposed.
const LAUNCH_TARGETS = [
  { id: "public_qa", label: "Public Q&A" },
  { id: "assistant", label: "AI Assistant" },
  { id: "chats", label: "YT Digital Twin" },
  { id: "ytre", label: "YTRE" },
];

// Where "Install this icon" sends the creator. `?it=` is read synchronously by
// the parse-time script in index.html, which is the only moment iOS will
// honour a manifest — so this MUST be a hard navigation, not SPA routing.
function installUrl(silo, target) {
  const base = {
    public_qa: `/s/${silo.slug}`,
    assistant: `/silo/${silo.slug}/assistant`,
    chats: `/dashboard/silo/${silo.id}?tab=yt-twin`,
    ytre: `/dashboard/silo/${silo.id}?tab=ytre`,
  }[target] || `/s/${silo.slug}`;
  return `${base}${base.includes("?") ? "&" : "?"}it=${target}`;
}

async function squareCompress(file) {
  // Returns a PNG data URL, ICON_SIZE × ICON_SIZE, center-cropped.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That doesn't look like a valid image."));
      img.onload = () => {
        const minSide = Math.min(img.width, img.height);
        const sx = (img.width - minSide) / 2;
        const sy = (img.height - minSide) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = ICON_SIZE;
        canvas.height = ICON_SIZE;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, ICON_SIZE, ICON_SIZE);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function SiloIconCard({ silo, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(silo.home_icon_data_url || null);
  const [target, setTarget] = useState(silo.home_icon_launch_target || "public_qa");
  const [hasPings, setHasPings] = useState(false);
  const inputRef = useRef(null);

  const letter = siloLetter(silo.name);

  // Informational only (Randy's call): a ping's notification tap navigates to
  // /s/<slug>?auto_q=… via sw.js, which OVERRIDES whatever the icon opens. So
  // the launch target never breaks pings — creators just need to know that.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/staging/alerts");
        setHasPings((r.data?.alerts || []).some(
          (a) => a.silo_id === silo.id && a.enabled !== false,
        ));
      } catch { /* silent — the note is a nicety, not a gate */ }
    })();
  }, [silo.id]);

  // Render preset thumbnails once per (preset, letter). renderPresetToDataUrl
  // caches internally, so the canvas work happens at most once each.
  const presetThumbs = useMemo(
    () => PRESETS.map((p) => ({ ...p, dataUrl: renderPresetToDataUrl(p, { letter }) })),
    [letter]
  );

  const save = async (dataUrl, successMsg) => {
    setBusy(true);
    try {
      await api.patch(`/silos/${silo.id}`, { home_icon_data_url: dataUrl });
      setPreview(dataUrl);
      toast.success(successMsg);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Could not save icon.");
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("File too big (max 4 MB before compression).");
      return;
    }
    try {
      const dataUrl = await squareCompress(file);
      if (dataUrl.length > 200 * 1024) {
        toast.error("Compressed icon is still too large. Try a simpler image.");
        return;
      }
      await save(dataUrl, "Icon saved.");
    } catch (err) {
      toast.error(err.message || "Could not read that image.");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the custom icon and fall back to the default?")) return;
    setBusy(true);
    try {
      await api.patch(`/silos/${silo.id}`, { home_icon_data_url: null });
      setPreview(null);
      toast("Reset to default icon.");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove icon.");
    } finally { setBusy(false); }
  };

  const saveTarget = async (next) => {
    const prev = target;
    setTarget(next);
    try {
      await api.patch(`/silos/${silo.id}`, { home_icon_launch_target: next });
      onSaved?.();
    } catch (err) {
      setTarget(prev);
      toast.error(err.response?.data?.detail || "Could not save the launch target.");
    }
  };

  return (
    <section className="bg-[#FDFCF8] border hairline rounded-lg p-6" data-testid="silo-icon-card">
      <div className="flex items-center gap-2 mb-1">
        <ImageIcon size={16} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">Home-screen icon</h3>
        <InfoTip text="When a visitor taps Share → Add to Home Screen on this silo's public page, iOS uses this image as the app icon on their phone. Tapping it later opens the silo directly. Square images work best (the upload tool will center-crop and shrink to 192×192). Leave empty to use the default My Silo icon." />
      </div>
      <p className="text-xs text-[#6B6A68] mb-4 max-w-2xl">
        Custom icon for this silo's "Add to Home Screen" experience. PNG / JPEG / WebP, automatically squared.
      </p>

      <div className="flex flex-wrap items-start justify-between gap-8">
        <div className="flex items-center gap-5">
          <div
            className="w-24 h-24 rounded-xl border hairline bg-white overflow-hidden flex items-center justify-center"
            data-testid="silo-icon-preview"
          >
            {preview ? (
              <img src={preview} alt="Silo icon preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-[10px] text-[#6B6A68] uppercase tracking-[0.18em]">No icon</div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onPick}
              data-testid="silo-icon-input"
              className="hidden"
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid="silo-icon-upload-btn"
              className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Spinner size={12} className="animate-spin" /> : <UploadSimple size={12} weight="duotone" />}
              {busy ? "Saving…" : preview ? "Replace icon" : "Upload icon"}
            </button>
            {preview && (
              <button
                onClick={remove}
                disabled={busy}
                data-testid="silo-icon-remove-btn"
                className="text-xs px-4 py-2 rounded-md border hairline text-[#C16E5A] hover:border-[#C16E5A] flex items-center gap-1.5"
              >
                <Trash size={12} /> Reset to default
              </button>
            )}
          </div>
        </div>

        {/* Oct 2026 (Randy pt-launch-target) — where the icon opens. */}
        <div className="flex flex-col gap-2 w-[280px] shrink-0" data-testid="silo-icon-launch-target">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A68]">This icon opens</span>
            <InfoTip text="Each icon permanently keeps the destination it was created with. Changing this only affects icons you install from now on — ones already on your home screen never change behaviour." />
          </div>
          <select
            value={target}
            onChange={(e) => saveTarget(e.target.value)}
            data-testid="silo-icon-target-select"
            className="text-sm px-3 py-2 rounded-md border hairline bg-white text-[#1C1B1A]"
          >
            {LAUNCH_TARGETS.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <button
            onClick={() => window.location.assign(installUrl(silo, target))}
            data-testid="silo-icon-install-btn"
            className="text-xs px-4 py-2 rounded-md border hairline text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center justify-center gap-1.5"
          >
            <DeviceMobile size={13} weight="duotone" /> Install this icon
          </button>
          <p className="text-[11px] text-[#6B6A68] leading-relaxed">
            Opens that page so you can finish with Share → Add to Home Screen.
            Icons already on your home screen keep opening where they were created.
          </p>
          {hasPings && (
            <p
              className="text-[11px] text-[#4A5D4E] leading-relaxed flex items-start gap-1.5 mt-1"
              data-testid="silo-icon-pings-note"
            >
              <Bell size={13} weight="duotone" className="shrink-0 mt-0.5" />
              Pings active in Staging. Notification taps always open Public QA.
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 pt-5 border-t hairline" data-testid="silo-icon-presets">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A68] mb-3">
          Or pick a preset
        </p>
        <div className="flex flex-wrap gap-3">
          {presetThumbs.map((p) => {
            const selected = preview === p.dataUrl;
            return (
              <button
                key={p.id}
                onClick={() => save(p.dataUrl, `Set "${p.label}" as the home-screen icon.`)}
                disabled={busy}
                data-testid={`silo-icon-preset-${p.id}`}
                title={p.label}
                className="group flex flex-col items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                    selected
                      ? "border-[#4A5D4E] shadow-md"
                      : "border-transparent group-hover:border-[#A7B3A0]"
                  }`}
                >
                  <img src={p.dataUrl} alt={p.label} className="w-full h-full object-cover" />
                </div>
                <span className={`text-[10px] uppercase tracking-[0.12em] ${selected ? "text-[#4A5D4E]" : "text-[#6B6A68]"}`}>
                  {p.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <SiloIconArtwork
        siloId={silo.id}
        letter={letter}
        onApplied={(dataUrl) => { setPreview(dataUrl); onSaved?.(); }}
      />
    </section>
  );
}
